import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronDown, CreditCard, Wallet, Coins, Users } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PartnersSection } from "@/components/loan/PartnersSection";
import { useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

const PienlainatLuottokortitPage = () => {
  const [activeTab, setActiveTab] = useState("pienlainat");
  const isMobile = useIsMobile();
  
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam && ['pienlainat', 'luottokortit', 'pikavippi', 'vertaislainat'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, []);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tab);
    window.history.replaceState({}, '', url.toString());
  };
  
  return (
    <div className="min-h-screen bg-white px-3 sm:px-8 py-5 sm:py-8">
      <div className="container mx-auto max-w-4xl">
        <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-blue-700">Pienlainat & Luottokortit</h1>
        
        <div className="bg-blue-50 p-4 sm:p-6 rounded-lg mb-6 sm:mb-8">
          <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-blue-700">Joustavat rahoitusratkaisut pienempiin tarpeisiin</h2>
          <p className="text-sm sm:text-base text-gray-700 mb-3 sm:mb-4">
            Pienlainat ja luottokortit tarjoavat joustavia rahoitusratkaisuja pienempiin ja lyhytaikaisiin rahoitustarpeisiin. 
            Ne soveltuvat erinomaisesti arjen yllättäviin menoihin tai pieniin hankintoihin.
          </p>
          <p className="text-sm sm:text-base text-gray-700">
            Lainasampo.fi:n kautta voit vertailla eri pankkien ja rahoituslaitosten tarjoamia pienlainoja ja luottokortteja 
            ja löytää itsellesi sopivimman vaihtoehdon. Palvelumme avulla säästät aikaa ja rahaa, sillä voit hakea useita 
            tarjouksia yhdellä hakemuksella.
          </p>
        </div>

        <div className="mb-6 sm:mb-8 overflow-x-auto">
          <div className="grid grid-cols-2 sm:flex sm:flex-row min-w-max gap-2 p-2 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100 shadow-sm">
            <a 
              href="#"
              onClick={(e) => { e.preventDefault(); handleTabChange("pienlainat"); }}
              className={`
                relative px-3 sm:px-5 py-2 sm:py-3 rounded-lg transition-all duration-300 font-medium text-center text-sm sm:text-base
                ${activeTab === "pienlainat" ? 
                  'bg-gradient-to-br from-purple-600 to-blue-700 text-white shadow-md' : 
                  'bg-white hover:bg-gradient-to-br hover:from-purple-100 hover:to-blue-100'
                }
              `}
            >
              <span className="flex items-center justify-center gap-1">
                <Wallet className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="whitespace-nowrap">Pienlainat</span>
              </span>
              {activeTab === "pienlainat" && (
                <span className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-purple-400 to-blue-400"></span>
              )}
            </a>
            
            <a 
              href="#"
              onClick={(e) => { e.preventDefault(); handleTabChange("luottokortit"); }}
              className={`
                relative px-3 sm:px-5 py-2 sm:py-3 rounded-lg transition-all duration-300 font-medium text-center text-sm sm:text-base
                ${activeTab === "luottokortit" ? 
                  'bg-gradient-to-br from-purple-600 to-blue-700 text-white shadow-md' : 
                  'bg-white hover:bg-gradient-to-br hover:from-purple-100 hover:to-blue-100'
                }
              `}
            >
              <span className="flex items-center justify-center gap-1">
                <CreditCard className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="whitespace-nowrap">Luottokortit</span>
              </span>
              {activeTab === "luottokortit" && (
                <span className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-purple-400 to-blue-400"></span>
              )}
            </a>
            
            <a 
              href="#"
              onClick={(e) => { e.preventDefault(); handleTabChange("pikavippi"); }}
              className={`
                relative px-3 sm:px-5 py-2 sm:py-3 rounded-lg transition-all duration-300 font-medium text-center text-sm sm:text-base
                ${activeTab === "pikavippi" ? 
                  'bg-gradient-to-br from-purple-600 to-blue-700 text-white shadow-md' : 
                  'bg-white hover:bg-gradient-to-br hover:from-purple-100 hover:to-blue-100'
                }
              `}
            >
              <span className="flex items-center justify-center gap-1">
                <Coins className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="whitespace-nowrap">Pienet lainat</span>
              </span>
              {activeTab === "pikavippi" && (
                <span className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-purple-400 to-blue-400"></span>
              )}
            </a>
            
            <a 
              href="#"
              onClick={(e) => { e.preventDefault(); handleTabChange("vertaislainat"); }}
              className={`
                relative px-3 sm:px-5 py-2 sm:py-3 rounded-lg transition-all duration-300 font-medium text-center text-sm sm:text-base
                ${activeTab === "vertaislainat" ? 
                  'bg-gradient-to-br from-purple-600 to-blue-700 text-white shadow-md' : 
                  'bg-white hover:bg-gradient-to-br hover:from-purple-100 hover:to-blue-100'
                }
              `}
            >
              <span className="flex items-center justify-center gap-1">
                <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="whitespace-nowrap">Vertaislainat</span>
              </span>
              {activeTab === "vertaislainat" && (
                <span className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-purple-400 to-blue-400"></span>
              )}
            </a>
          </div>
        </div>
          
        <PartnersSection activeTab={activeTab} />
          
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
          <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-blue-700">Rahoitusvaihtoehtojen vertailu - miten löydät edullisimman?</h3>
          <p className="text-sm sm:text-base text-gray-700 mb-4">
            Lainojen ja luottojen koroissa ja ehdoissa voi olla huomattavia eroja eri lainanantajien välillä. 
            Tämän vuoksi tarjousten vertailu on erittäin tärkeää ennen päätöksen tekemistä.
          </p>
          
          <h4 className="font-medium text-gray-800 mt-4 mb-2">Näitä asioita kannattaa vertailla:</h4>
          <ol className="list-decimal pl-5 space-y-1 sm:space-y-2 text-sm sm:text-base text-gray-700">
            <li><span className="font-medium">Todellinen vuosikorko</span> - Tämä kertoo lainan kokonaiskustannukset vuositasolla ja on paras vertailukohta eri lainojen välillä</li>
            <li><span className="font-medium">Kuukausierät</span> - Varmista, että kuukausierät sopivat budjettiisi</li>
            <li><span className="font-medium">Laina-aika</span> - Pidempi laina-aika pienentää kuukausieriä, mutta nostaa kokonaiskustannuksia</li>
            <li><span className="font-medium">Nostoprovisiot ja muut kulut</span> - Tarkista kaikki lainaan liittyvät lisäkulut</li>
            <li><span className="font-medium">Joustavuus</span> - Onko mahdollisuus maksaa laina ennenaikaisesti takaisin ilman lisäkuluja?</li>
          </ol>
          
          <p className="text-sm sm:text-base text-gray-700 mt-4">
            Lainasampo.fi:n avulla voit helposti vertailla eri lainantarjoajien vaihtoehoja ja löytää itsellesi 
            edullisimman vaihtoehdon. Täyttämällä yhden hakemuksen saat tarjoukset usealta lainanantajalta 
            ja voit vertailla niitä rinnakkain.
          </p>
        </div>
        
        {activeTab === "pienlainat" && (
          <div className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <Card>
                <CardHeader className="px-4 py-3 sm:p-6">
                  <CardTitle className="text-blue-700 flex items-center text-lg sm:text-xl">
                    <Wallet className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                    Pienlainat
                  </CardTitle>
                  <CardDescription>Nopea rahoitus pieniin tarpeisiin</CardDescription>
                </CardHeader>
                <CardContent className="px-4 pb-4 sm:p-6 sm:pt-0 text-sm sm:text-base">
                  <h3 className="font-medium text-gray-800 mb-2">Pienlainojen ominaisuudet:</h3>
                  <ul className="list-disc pl-5 space-y-1 sm:space-y-2 text-gray-700">
                    <li>Lainasummat tyypillisesti 100-2000 euroa</li>
                    <li>Lyhyt laina-aika, yleensä 1-12 kuukautta</li>
                    <li>Nopea hakuprosessi ja päätös</li>
                    <li>Ei vaadi vakuuksia tai takaajia</li>
                    <li>Joustavat takaisinmaksuehdot</li>
                  </ul>
                  
                  <h3 className="font-medium text-gray-800 mt-4 mb-2">Pienlainojen hakeminen:</h3>
                  <ol className="list-decimal pl-5 space-y-1 sm:space-y-2 text-gray-700">
                    <li>Täytä lainahakemus verkossa Lainasampo.fi:ssä</li>
                    <li>Vertaile eri lainanantajien tarjouksia</li>
                    <li>Valitse itsellesi sopivin pienlaina</li>
                    <li>Tunnistaudu verkkopankkitunnuksilla</li>
                    <li>Rahat siirretään tilillesi nopeasti päätöksen jälkeen</li>
                  </ol>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="px-4 py-3 sm:p-6">
                  <CardTitle className="text-blue-700 text-lg sm:text-xl">Milloin pienlaina kannattaa?</CardTitle>
                  <CardDescription>Pienlaina soveltuu moniin tilanteisiin</CardDescription>
                </CardHeader>
                <CardContent className="px-4 pb-4 sm:p-6 sm:pt-0 text-sm sm:text-base">
                  <p className="text-gray-700 mb-4">
                    Pienlaina soveltuu parhaiten tilanteisiin, joissa tarvitset nopeasti pienehkön summan rahaa 
                    lyhyeksi aikaa. Tällaisia tilanteita voivat olla esimerkiksi:
                  </p>
                  <ul className="list-disc pl-5 space-y-1 sm:space-y-2 text-gray-700">
                    <li>Yllättävät menot, kuten kodinkoneen rikkoutuminen</li>
                    <li>Laskujen maksaminen palkkapäivään asti</li>
                    <li>Pienet hankinnat, kuten elektroniikka tai huonekalut</li>
                    <li>Harrastusvälineiden ostaminen</li>
                    <li>Lomamatkan tai juhlatilaisuuden rahoittaminen</li>
                  </ul>
                  
                  <p className="text-gray-700 mt-4">
                    On kuitenkin tärkeää huomioida, että pienlainoissa korot ovat usein korkeampia kuin suuremmissa 
                    lainoissa. Siksi pienlaina kannattaa ottaa vain lyhytaikaiseen tarpeeseen ja varmistaa, että 
                    pystyt maksamaan sen takaisin sovitussa aikataulussa.
                  </p>
                </CardContent>
              </Card>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
              <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-blue-700">Pienlainojen vertailu - miten löydät edullisimman?</h3>
              <p className="text-sm sm:text-base text-gray-700 mb-4">
                Pienlainojen koroissa ja ehdoissa voi olla huomattavia eroja eri lainanantajien välillä. 
                Tämän vuoksi lainatarjousten vertailu on erittäin tärkeää ennen päätöksen tekemistä.
              </p>
              
              <h4 className="font-medium text-gray-800 mt-4 mb-2">Näitä asioita kannattaa vertailla:</h4>
              <ol className="list-decimal pl-5 space-y-1 sm:space-y-2 text-sm sm:text-base text-gray-700">
                <li><span className="font-medium">Todellinen vuosikorko</span> - Tämä kertoo lainan kokonaiskustannukset vuositasolla ja on paras vertailukohta eri lainojen välillä</li>
                <li><span className="font-medium">Kuukausierät</span> - Varmista, että kuukausierät sopivat budjettiisi</li>
                <li><span className="font-medium">Laina-aika</span> - Pidempi laina-aika pienentää kuukausieriä, mutta nostaa kokonaiskustannuksia</li>
                <li><span className="font-medium">Nostoprovisiot ja muut kulut</span> - Tarkista kaikki lainaan liittyvät lisäkulut</li>
                <li><span className="font-medium">Joustavuus</span> - Onko mahdollisuus maksaa laina ennenaikaisesti takaisin ilman lisäkuluja?</li>
              </ol>
              
              <p className="text-sm sm:text-base text-gray-700 mt-4">
                Lainasampo.fi:n avulla voit helposti vertailla eri lainantarjoajien pienlainoja ja löytää itsellesi 
                edullisimman vaihtoehdon. Täyttämällä yhden hakemuksen saat tarjoukset usealta lainanantajalta 
                ja voit vertailla niitä rinnakkain.
              </p>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
              <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-blue-700">Pienlainat vs. Pikavippi - mitä eroa?</h3>
              <p className="text-sm sm:text-base text-gray-700 mb-4">
                Termejä "pienlaina" ja "pikavippi" käytetään usein toistensa synonyymeinä, mutta niiden välillä on 
                joitakin eroja, jotka on hyvä tietää ennen lainan hakemista.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-blue-700 mb-2">Pienlaina:</h4>
                  <ul className="list-disc pl-5 space-y-2 text-gray-700">
                    <li>Lainasummat yleensä 100-2000 euroa</li>
                    <li>Laina-aika 1-12 kuukautta</li>
                    <li>Usein pienempi todellinen vuosikorko</li>
                    <li>Takaisinmaksu tapahtuu kuukausierissä</li>
                    <li>Lainanantajat usein tunnettuja rahoituslaitoksia</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-medium text-blue-700 mb-2">Pikavippi:</h4>
                  <ul className="list-disc pl-5 space-y-2 text-gray-700">
                    <li>Lainasummat tyypillisesti 50-1000 euroa</li>
                    <li>Lyhyempi laina-aika, usein 14-60 päivää</li>
                    <li>Korkeampi todellinen vuosikorko</li>
                    <li>Takaisinmaksu usein yhdessä erässä</li>
                    <li>Lainanantajat voivat olla vähemmän tunnettuja</li>
                  </ul>
                </div>
              </div>
              
              <p className="text-sm sm:text-base mt-4">
                Nykyisin lainsäädäntö on tiukentunut, ja kuluttajaluottojen korkokatot ja muut säännökset 
                ovat tehneet erot pienlainojen ja pikavippien välillä pienemmiksi. Kummassakin tapauksessa 
                on tärkeää vertailla eri vaihtoehoja ja varmistaa, että ymmärrät täysin lainan ehdot 
                ennen hakemuksen lähettämistä.
              </p>
            </div>
          </div>
        )}
        
        {activeTab === "luottokortit" && (
          <div className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <Card>
                <CardHeader className="px-4 py-3 sm:p-6">
                  <CardTitle className="text-blue-700 flex items-center text-lg sm:text-xl">
                    <CreditCard className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                    Luottokortit
                  </CardTitle>
                  <CardDescription>Joustava maksutapa päivittäisiin ostoihin</CardDescription>
                </CardHeader>
                <CardContent className="px-4 pb-4 sm:p-6 sm:pt-0 text-sm sm:text-base">
                  <h3 className="font-medium text-gray-800 mb-2">Luottokorttien ominaisuudet:</h3>
                  <ul className="list-disc pl-5 space-y-1 sm:space-y-2 text-gray-700">
                    <li>Jatkuva luotto, jota voi käyttää ostosten maksamiseen</li>
                    <li>Luottorajat tyypillisesti 1000-10000 euroa</li>
                    <li>Maksuaika ostoksille jopa 30-45 päivää korottomasti</li>
                    <li>Mahdollisuus jakaa isompia ostoksia eriin</li>
                    <li>Usein lisäetuja, kuten bonuspisteitä tai matkavakuutus</li>
                  </ul>
                  
                  <h3 className="font-medium text-gray-800 mt-4 mb-2">Luottokorttien hakeminen:</h3>
                  <ol className="list-decimal pl-5 space-y-1 sm:space-y-2 text-gray-700">
                    <li>Vertaile eri luottokorttien ominaisuuksia ja etuja</li>
                    <li>Täytä luottokorttihakemus verkossa</li>
                    <li>Tunnistaudu verkkopankkitunnuksilla</li>
                    <li>Luottokortti toimitetaan kotiisi hyväksytyn päätöksen jälkeen</li>
                    <li>Aktivoi kortti ja aloita sen käyttö</li>
                  </ol>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="px-4 py-3 sm:p-6">
                  <CardTitle className="text-blue-700">Milloin luottokortti on hyvä valinta?</CardTitle>
                  <CardDescription>Luottokortin edut ja käyttökohteet</CardDescription>
                </CardHeader>
                <CardContent className="px-4 pb-4 sm:p-6 sm:pt-0 text-sm sm:text-base">
                  <p className="text-gray-700 mb-4">
                    Luottokortti on joustava maksutapa, joka soveltuu moniin arkipäivän tilanteisiin. 
                    Luottokortti on erityisen hyödyllinen seuraavissa tilanteissa:
                  </p>
                  <ul className="list-disc pl-5 space-y-1 sm:space-y-2 text-gray-700">
                    <li>Verkko-ostokset ja -varaukset - lisää turvaa maksuihin</li>
                    <li>Matkustaminen - useimmat hotellit ja autovuokraamot vaativat luottokortin</li>
                    <li>Isompien hankintojen jaksottaminen eriin</li>
                    <li>Kuukauden lopun yllättävät menot ennen palkkapäivää</li>
                    <li>Bonuspisteiden ja etujen kerääminen säännöllisistä ostoista</li>
                  </ul>
                  
                  <p className="text-gray-700 mt-4">
                    Luottokortin vastuullinen käyttö edellyttää hyvää taloudenhallintaa. Suositeltavaa on 
                    maksaa luottokortin saldo kokonaan pois laskun eräpäivänä, jotta vältät korkomenot.
                  </p>
                </CardContent>
              </Card>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
              <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-blue-700">Luottokorttien vertailu - löydä itsellesi sopivin</h3>
              <p className="text-sm sm:text-base text-gray-700 mb-4">
                Luottokortteja on markkinoilla lukuisia erilaisia, ja niiden ominaisuudet, edut ja kustannukset 
                vaihtelevat merkittävästi. Oikean luottokortin valinta perustuu omiin tarpeisiisi ja käyttötottumuksiisi.
              </p>
              
              <h4 className="font-medium text-gray-800 mt-4 mb-2">Näitä asioita kannattaa vertailla:</h4>
              <ol className="list-decimal pl-5 space-y-1 sm:space-y-2 text-sm sm:text-base text-gray-700">
                <li><span className="font-medium">Vuosimaksu</span> - Onko kortilla vuosimaksua, ja jos on, vastaako kortin edut maksun suuruutta?</li>
                <li><span className="font-medium">Korko</span> - Mikä on luoton korko, jos et maksa saldoa kokonaan pois?</li>
                <li><span className="font-medium">Koroton maksuaika</span> - Kuinka pitkä koroton maksuaika ostoksille myönnetään?</li>
                <li><span className="font-medium">Bonus- ja palkintojärjestelmät</span> - Mitä etuja saat kortin käytöstä?</li>
                <li><span className="font-medium">Lisäpalvelut</span> - Sisältääkö kortti esimerkiksi matkavakuutuksen tai muita lisäpalveluita?</li>
              </ol>
              
              <p className="text-sm sm:text-base text-gray-700 mt-4">
                Lainasampo.fi:n avulla voit vertailla eri pankkien ja rahoituslaitosten luottokorttitarjouksia 
                ja löytää itsellesi parhaiten sopivan vaihtoehdon. Vertailemalla tarjouksia voit säästää 
                vuosimaksuissa ja korkokuluissa sekä löytää kortin, joka tarjoaa juuri sinulle hyödyllisimmät edut.
              </p>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
              <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-blue-700">Luottokortti vs. debit-kortti - kumpi on sinulle sopivampi?</h3>
              <p className="text-sm sm:text-base text-gray-700 mb-4">
                Luottokortit ja debit-kortit ovat molemmat yleisiä maksutapoja, mutta niiden toiminnassa on 
                merkittäviä eroja. Ymmärtämällä erot voit päättää, kumpi korttityyppi sopii paremmin tarpeisiisi.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-blue-700 mb-2">Luottokortti:</h4>
                  <ul className="list-disc pl-5 space-y-2 text-gray-700">
                    <li>Käytät pankin tai rahoituslaitoksen rahaa</li>
                    <li>Maksat ostokset takaisin myöhemmin</li>
                    <li>Koroton maksuaika yleensä 30-45 päivää</li>
                    <li>Usein bonusjärjestelmiä ja lisäetuja</li>
                    <li>Parempi turva petoksia ja virheellisiä veloituksia vastaan</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-medium text-blue-700 mb-2">Debit-kortti:</h4>
                  <ul className="list-disc pl-5 space-y-2 text-gray-700">
                    <li>Käytät omia rahojasi suoraan pankkitililtä</li>
                    <li>Raha veloitetaan tililtäsi välittömästi</li>
                    <li>Ei mahdollisuutta velkaantua</li>
                    <li>Harvemmin vuosimaksuja</li>
                    <li>Sopii päivittäisiin ostoksiin ja käteisnostoihin</li>
                  </ul>
                </div>
              </div>
              
              <p className="text-sm sm:text-base mt-4">
                Monilla on käytössään sekä luotto- että debit-kortti, ja niitä käytetään eri tilanteissa. 
                Luottokortti soveltuu erityisesti verkko-ostoksiin, matkoihin ja suurempiin hankintoihin, 
                kun taas debit-kortti on hyvä vaihtoehto päivittäisiin ostoksiin, jos haluat pitää talouden 
                tiukasti hallinnassa.
              </p>
            </div>
          </div>
        )}
        
        {activeTab === "pikavippi" && (
          <div className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <Card>
                <CardHeader className="px-4 py-3 sm:p-6">
                  <CardTitle className="text-blue-700 flex items-center text-lg sm:text-xl">
                    <Coins className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                    Pienet lainat (30-500€)
                  </CardTitle>
                  <CardDescription>Nopea rahoitus pieniin ja yllättäviin menoihin</CardDescription>
                </CardHeader>
                <CardContent className="px-4 pb-4 sm:p-6 sm:pt-0 text-sm sm:text-base">
                  <h3 className="font-medium text-gray-800 mb-2">Pienten lainojen ominaisuudet:</h3>
                  <ul className="list-disc pl-5 space-y-1 sm:space-y-2 text-gray-700">
                    <li>Lainasummat tyypillisesti 30-500 euroa</li>
                    <li>Erittäin lyhyt laina-aika, usein 14-60 päivää</li>
                    <li>Nopea hakuprosessi ja välitön päätös</li>
                    <li>Ei vaadi vakuuksia tai takaajia</li>
                    <li>Rahat tilillä usein saman päivän aikana</li>
                  </ul>
                  
                  <h3 className="font-medium text-gray-800 mt-4 mb-2">Käyttökohteet:</h3>
                  <ul className="list-disc pl-5 space-y-1 sm:space-y-2 text-gray-700">
                    <li>Yllättävät laskut tai menot</li>
                    <li>Tilapäinen rahapula ennen palkkapäivää</li>
                    <li>Pienet, välttämättömät hankinnat</li>
                    <li>Lyhytaikaiset rahantarpeet</li>
                  </ul>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="px-4 py-3 sm:p-6">
                  <CardTitle className="text-blue-700">Milloin pieni laina kannattaa?</CardTitle>
                  <CardDescription>Sopii vain lyhytaikaiseen tarpeeseen</CardDescription>
                </CardHeader>
                <CardContent className="px-4 pb-4 sm:p-6 sm:pt-0 text-sm sm:text-base">
                  <p className="text-gray-700 mb-4">
                    Pieni laina soveltuu parhaiten tilanteisiin, joissa tarvitset nopeasti pienen summan rahaa 
                    hyvin lyhyeksi aikaa ja olet varma takaisinmaksukyvystäsi lähiaikoina.
                  </p>
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                    <h4 className="text-yellow-800 font-medium mb-1">Huomioitavaa pienten lainojen kanssa:</h4>
                    <ul className="list-disc pl-5 space-y-1 text-yellow-800">
                      <li>Todellinen vuosikorko on usein hyvin korkea</li>
                      <li>Tarkoitettu vain lyhytaikaiseen rahoitustarpeeseen</li>
                      <li>Maksuviivästykset voivat aiheuttaa merkittäviä lisäkuluja</li>
                      <li>Ei sovellu jatkuvaan rahoitustarpeeseen</li>
                    </ul>
                  </div>
                  <p className="text-gray-700">
                    Varmista aina ennen hakemista, että pystyt maksamaan lainan takaisin sovitussa aikataulussa. 
                    Jos tarvitset rahaa pidemmäksi aikaa, kannattaa harkita edullisempaa pienlainaa tai kulutusluottoa.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
        
        {activeTab === "vertaislainat" && (
          <div className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <Card>
                <CardHeader className="px-4 py-3 sm:p-6">
                  <CardTitle className="text-blue-700 flex items-center text-lg sm:text-xl">
                    <Users className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                    Vertaislainat
                  </CardTitle>
                  <CardDescription>Lainaa suoraan yksityishenkilöiltä ilman pankkia välikätenä</CardDescription>
                </CardHeader>
                <CardContent className="px-4 pb-4 sm:p-6 sm:pt-0 text-sm sm:text-base">
                  <h3 className="font-medium text-gray-800 mb-2">Vertaislainojen ominaisuudet:</h3>
                  <ul className="list-disc pl-5 space-y-1 sm:space-y-2 text-gray-700">
                    <li>Lainasummat tyypillisesti 500-20 000 euroa</li>
                    <li>Laina-aika 1-5 vuotta</li>
                    <li>Ihmiseltä ihmiselle -lainaaminen</li>
                    <li>Mahdollisuus saada laina myös haastavammassa taloustilanteessa</li>
                    <li>Usein kilpailukykyiset korot verrattuna perinteisiin pankkilainoihin</li>
                  </ul>
                  
                  <h3 className="font-medium text-gray-800 mt-4 mb-2">Vertaislainan hakeminen:</h3>
                  <ol className="list-decimal pl-5 space-y-1 sm:space-y-2 text-gray-700">
                    <li>Täytä hakemus vertaislainapalvelussa</li>
                    <li>Sijoittajat arvioivat lainahakemuksesi</li>
                    <li>Sijoittajat päättävät, rahoittavatko hakemuksesi</li>
                    <li>Useampi sijoittaja voi rahoittaa yhden lainahakemuksen</li>
                    <li>Lainan myöntämisen jälkeen rahat siirretään tilillesi</li>
                  </ol>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="px-4 py-3 sm:p-6">
                  <CardTitle className="text-blue-700">Vertaislainojen edut ja haasteet</CardTitle>
                  <CardDescription>Vaihtoehto perinteiselle pankkilainalle</CardDescription>
                </CardHeader>
                <CardContent className="px-4 pb-4 sm:p-6 sm:pt-0 text-sm sm:text-base">
                  <h3 className="font-medium text-gray-800 mb-2">Edut:</h3>
                  <ul className="list-disc pl-5 space-y-1 sm:space-y-2 text-gray-700">
                    <li>Mahdollisuus saada lainaa, vaikka pankit olisivat kieltäytyneet</li>
                    <li>Usein kilpailukykyinen korko</li>
                    <li>Joustava lainaprosessi</li>
                    <li>Nopea lainapäätös</li>
                    <li>Läpinäkyvyys sijoittajien ja lainaajien välillä</li>
                  </ul>
                  
                  <h3 className="font-medium text-gray-800 mt-4 mb-2">Huomioitavaa:</h3>
                  <ul className="list-disc pl-5 space-y-1 sm:space-y-2 text-gray-700">
                    <li>Lainan saatavuus riippuu sijoittajien kiinnostuksesta</li>
                    <li>Korot voivat vaihdella lainaajan riskiprofiilin mukaan</li>
                    <li>Vertaislainapalvelut perivät välityspalkkion</li>
                    <li>Maksuhäiriöt vaikuttavat luottoluokitukseen samoin kuin perinteisissä lainoissa</li>
                  </ul>
                  
                  <p className="text-gray-700 mt-4">
                    Vertaislainat tarjoavat vaihtoehtoisen rahoitustavan erityisesti henkilöille, joilla on vaikeuksia
                    saada lainaa perinteisistä pankeista. Vertaislainat voivat olla myös hyvä vaihtoehto lainojen
                    yhdistämiseen.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
        
        <div className="mt-6 sm:mt-8">
          <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-blue-700">Usein kysytyt kysymykset</h2>
          <Accordion type="single" collapsible className="bg-white border border-gray-200 rounded-lg">
            <AccordionItem value="item-1">
              <AccordionTrigger className="px-4 sm:px-6 py-3 text-sm sm:text-base">Miten pienlainan hakeminen vaikuttaa luottotietoihin?</AccordionTrigger>
              <AccordionContent className="px-4 sm:px-6 text-sm sm:text-base">
                <p className="text-gray-700">
                  Pienlainan hakeminen itsessään ei vaikuttaa luottotietoihin, mutta lainanantaja tarkistaa luottotietosi 
                  hakemuksen käsittelyn yhteydessä. Jos laina myönnetään, siitä voi tulla merkintä positiiviseen 
                  luottorekisteriin, mikä ei ole negatiivinen asia. Vain maksuhäiriöt tai lainan maksamatta jättäminen 
                  voivat johtaa negatiiviseen merkintään luottotietorekisterissä.
                </p>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-2">
              <AccordionTrigger className="px-4 sm:px-6 py-3 text-sm sm:text-base">Kuinka nopeasti saan rahat tililleni pienlainan hyväksymisen jälkeen?</AccordionTrigger>
              <AccordionContent className="px-4 sm:px-6 text-sm sm:text-base">
                <p className="text-gray-700">
                  Pienlainojen käsittelyajat ovat yleensä hyvin nopeita. Useimmat lainanantajat siirtävät rahat tilillesi 
                  saman päivän aikana, kun lainapäätös on hyväksytty ja sopimukset allekirjoitettu. Osa lainanantajista 
                  tarjoaa jopa pikamaksua, jolloin rahat voivat olla tililläsi muutamassa minuutissa hyväksymisen jälkeen. 
                  Pankkien väliset siirtoajat voivat kuitenkin vaikuttaa lopulliseen aikatauluun.
                </p>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-3">
              <AccordionTrigger className="px-4 sm:px-6 py-3 text-sm sm:text-base">Mitä tapahtuu, jos en pysty maksamaan luottokorttilaskuani eräpäivänä?</AccordionTrigger>
              <AccordionContent className="px-4 sm:px-6 text-sm sm:text-base">
                <p className="text-gray-700">
                  Jos et pysty maksamaan luottokorttilaskuasi kokonaan eräpäivänä, maksamatta jääneelle summalle alkaa 
                  kertyä korkoa. Tämä korko on yleensä huomattavasti korkeampi kuin monissa muissa lainamuodoissa. 
                  Lisäksi voit joutua maksamaan viivästysmaksuja. On tärkeää olla yhteydessä luottokortin myöntäjään 
                  mahdollisimman pian, jos kohtaat maksuvaikeuksia. Usein on mahdollista sopia joustavammasta maksuaikataulusta.
                </p>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-4">
              <AccordionTrigger className="px-4 sm:px-6 py-3 text-sm sm:text-base">Voiko pienlainaa tai luottokorttia saada, jos luottotiedoissa on merkintä?</AccordionTrigger>
              <AccordionContent className="px-4 sm:px-6 text-sm sm:text-base">
                <p className="text-gray-700">
                  Maksuhäiriömerkintä luottotiedoissa vaikeuttaa merkittävästi pienlainan tai luottokortin saamista. 
                  Useimmat perinteiset pankit ja rahoituslaitokset eivät myönnä lainaa tai luottokorttia, jos hakijalla 
                  on maksuhäiriömerkintä. On kuitenkin olemassa joitakin rahoitusyhtiöitä, jotka tarjoavat erityisiä 
                  lainatuotteita henkilöille, joilla on maksuhäiriömerkintä. Näissä lainoissa on yleensä korkeampi korko 
                  ja tiukemmat ehdot.
                </p>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-5">
              <AccordionTrigger className="px-4 sm:px-6 py-3 text-sm sm:text-base">Millä perusteella luottokortin luottoraja määräytyy?</AccordionTrigger>
              <AccordionContent className="px-4 sm:px-6 text-sm sm:text-base">
                <p className="text-gray-700">
                  Luottokortin luottoraja määräytyy useamman tekijän perusteella. Pankki tai rahoituslaitos arvioi 
                  hakijan maksukykyä tulotason, työsuhteen pysyvyyden, aiemman maksukäyttäytymisen ja luottohistorian 
                  perusteella. Myös muut olemassa olevat luotot ja lainat vaikuttavat arvioinnissa. Uuden luottokortin 
                  kohdalla luottoraja on usein alhaisempi, mutta sitä voidaan nostaa myöhemmin, kun kortinhaltija on 
                  osoittanut vastuullista maksukäyttäytymistä.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
        
        <div className="bg-blue-600 text-white p-4 sm:p-6 rounded-lg mt-6 sm:mt-8">
          <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Vertaile pienlainoja ja luottokortteja!</h2>
          <p className="text-sm sm:text-base mb-4">
            Vertailemalla eri rahoitusvaihtoehtoja voit löytää juuri sinulle sopivimman ja edullisimman pienlainan tai luottokortin. 
            Täytä hakemus nyt!
          </p>
          <a 
            href="/kilpailuta" 
            className="inline-block bg-white text-blue-600 px-4 sm:px-6 py-2 rounded-md font-medium hover:bg-blue-50 transition-colors text-sm sm:text-base"
          >
            Vertaile vaihtoehtoja
          </a>
        </div>
      </div>
    </div>
  );
};

export default PienlainatLuottokortitPage;
